<template>
  <div>
    <AsyncModal
      :fetch="fetchWarrantyServices"
      :name="MODAL_NAMES.PERKS"
      :title="i18n(translations.title)"
    >
      <template #content>
        <div>
          <h3 v-if="warranty.title" class="heading-3">
            {{ warranty.title }}
          </h3>

          <div v-if="shouldShowServices" class="mt-16">
            <ContentRow
              v-for="service in services"
              :key="service.title"
              :hyperlink="service.hyperlink"
              :labels="service.texts"
              :title="service.title"
            >
              <component :is="service.icon"></component>
            </ContentRow>
          </div>
        </div>
        <WarrantyExclusion />
      </template>
    </AsyncModal>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import AsyncModal from '@backmarket/nuxt-layer-product-modals/AsyncModal.vue'
import ContentRow from '@backmarket/nuxt-layer-product-modals/ContentRow.vue'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { IconCalendar } from '@ds/icons/IconCalendar'
import { IconCreditCard } from '@ds/icons/IconCreditCard'
import { IconExport } from '@ds/icons/IconExport'
import { IconHeart } from '@ds/icons/IconHeart'
import { IconTools } from '@ds/icons/IconTools'

import type { GetWarrantyServicesResponse } from '~/api/product-page/types'
import { useUrlParams } from '~/scopes/product/composables/useUrlParams'
import { MODAL_NAMES } from '~/scopes/product/constants'

import WarrantyExclusion from '../../../WarrantyExclusion/WarrantyExclusion.vue'

import translations from './PerksModal.translations'

const ICONS: Record<string, unknown> = {
  warranty: IconTools,
  return: IconCalendar,
  shipping: IconExport,
  customerCare: IconHeart,
  instalmentPayment: IconCreditCard,
}

const props = defineProps<{
  listingId: number
}>()

const warranty = ref()
const i18n = useI18n()
const { productId } = useUrlParams()
const $httpFetch = useHttpFetch()

const shouldShowServices = computed(() => {
  return !isEmpty(warranty.value.includedServices)
})

const services = computed(() => {
  return (
    shouldShowServices.value &&
    warranty.value.includedServices.map(
      ({
        type,
        title,
        texts,
        hyperlink,
      }: GetWarrantyServicesResponse['includedServices'][number]) => {
        return {
          icon: ICONS[type],
          title,
          texts,
          hyperlink,
        }
      },
    )
  )
})

async function fetchWarrantyServices() {
  try {
    const data = await $httpFetch(
      '/bm/product/:id/listing/:listingId/warranty_services',
      {
        pathParams: {
          id: productId,
          listingId: props.listingId,
        },
      },
    )
    warranty.value = data
  } catch {
    // Do nothing
  }
}
</script>
